import { AxiosResponse } from 'axios';
import { SPECIMENS_API_ENDPOINTS, SPECIMENS_LOCATIONS_API_ENDPOINTS } from '../constants/apiEndpoints';
import { Account } from '../types/interfaces';
import {
  GetAccountAnimalParams,
  GetVariancesResponse,
  SpecimensInventoryParams,
  StandardParams,
} from '../types/interfaces/apiParams.interfaces';
import {
  Specimen,
  SpecimenInventory,
  SpecimenInventoryWithAccount,
  SpecimenInventoryWithUseRequested,
  SpecimenLocation,
  SpecimenVarianceSummary,
  SpecimenWithOwnership,
} from '../types/interfaces/specimen.interfaces';
import { ExistingEmbryoSpecimen } from '../types/interfaces/addStrawLevelData.interfaces';
import { appContextGet, appContextPut } from './httpCommon/appAxios.service';
import qs from 'qs';

export const getInventoryVarianceSummary = (specimenId: number): Promise<AxiosResponse<SpecimenVarianceSummary, any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_VARIANCE_SUMMARY + specimenId.toString());
};

export const getQuantityInCanister = (
  canisterId: number | string,
  accountId: number | string,
  specimenId: number | string,
  params?: {},
): Promise<AxiosResponse<SpecimenInventory[], any>> => {
  return appContextGet(
    SPECIMENS_API_ENDPOINTS.SPECIMENS +
      specimenId +
      SPECIMENS_API_ENDPOINTS.ACCOUNTS_ +
      accountId +
      SPECIMENS_API_ENDPOINTS.CANISTERS +
      canisterId,
      params
  );
};

export const getRemovedCount = (specimenId: number): Promise<AxiosResponse<number, any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.SPECIMENS + specimenId.toString() + SPECIMENS_API_ENDPOINTS.REMOVED_COUNT);
};

export const getSpecimen = (id: number, params?: { include: string }): Promise<AxiosResponse<Specimen, any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET + '/' + id, params);
};

export const getSpecimenLocations = (params?: any): Promise<AxiosResponse<SpecimenLocation[], any>> => {
  return appContextGet(SPECIMENS_LOCATIONS_API_ENDPOINTS.GET, params);
};

export const getSpecimenOwnerByAccountId = (
  specimenId: number | string,
  accountId: number | string,
): Promise<AxiosResponse<SpecimenInventoryWithAccount[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.SPECIMENS + specimenId + SPECIMENS_API_ENDPOINTS.ACCOUNTS_ + accountId);
};

export const getSpecimenOwners = (specimenId: number | string, params?: {}): Promise<AxiosResponse<Account[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.SPECIMENS + specimenId + SPECIMENS_API_ENDPOINTS.ACCOUNTS, params);
};

export const getExistingEmbryoSpecimens = (
  specimenId: number,
): Promise<AxiosResponse<ExistingEmbryoSpecimen[], any>> => {
  return appContextGet(
    `${SPECIMENS_API_ENDPOINTS.SPECIMENS}${specimenId}${SPECIMENS_API_ENDPOINTS.GET_EXISTING_EMBRYO_SPECIMENS}`,
  );
};

export const getSpecimens = (params?: StandardParams): Promise<AxiosResponse<Specimen[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET, params);
};

export const getSpecimensByAccount = (
  accountId: number,
  params?: GetAccountAnimalParams,
): Promise<AxiosResponse<SpecimenInventoryWithAccount[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_ACCOUNT_INVENTORY + accountId, params);
};

export const getSpecimensByAnimal = (params?: {}): Promise<AxiosResponse<Specimen, any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET, params);
};

export const getSpecimensByOwnership = (
  animalId: number | string,
  accountId: number | string,
  params?: {},
): Promise<AxiosResponse<SpecimenInventory[], any>> => {
  return appContextGet(
    `${SPECIMENS_API_ENDPOINTS.GET_ACCOUNTS}${accountId}${SPECIMENS_API_ENDPOINTS.ANIMALS}${animalId}`,
    undefined,
    {
      paramsSerializer: (p: Record<string, any>) => qs.stringify(p, { arrayFormat: 'repeat' }),
      params,
    },
  );
};

export const getSpecimensInventory = (params?: SpecimensInventoryParams): Promise<AxiosResponse<Specimen[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_INVENTORY, params);
};

export const getSpecimensInventoryByCanisterId = (canisterId: number | string): Promise<AxiosResponse<Specimen[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET + SPECIMENS_API_ENDPOINTS.CANISTERS + canisterId);
};

export const getUsePendingSpecimensByAccountId = (
  accountId: number | string,
  params?: StandardParams,
): Promise<AxiosResponse<SpecimenInventoryWithUseRequested[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_ACCOUNTS + accountId + SPECIMENS_API_ENDPOINTS.USE_PENDING, params);
};

export const getUsePendingSpecimensByCanisterId = (
  canisterId: number | string,
  params?: StandardParams,
): Promise<AxiosResponse<SpecimenWithOwnership[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_USE_PENDING + '/' + canisterId, params);
};

export const getVariance = (specimenId: number): Promise<AxiosResponse<number, any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_VARIANCE_ + specimenId.toString());
};

export const getVariances = (params?: {}): Promise<AxiosResponse<GetVariancesResponse[], any>> => {
  return appContextGet(SPECIMENS_API_ENDPOINTS.GET_VARIANCES, params);
};

export const updateSpecimen = (id: number, payload: {}): Promise<AxiosResponse<{}, any>> => {
  return appContextPut(SPECIMENS_API_ENDPOINTS.UPDATE + id, payload);
};
